import { useState, type FormEvent } from 'react';
import { fetchApi } from '@/lib/api';
import { trackHq } from './analytics';

export function EmailCapture({
  guideSlug,
  guideTitle,
}: {
  guideSlug: string;
  guideTitle: string;
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed.includes('@')) {
      setError('Enter a valid email.');
      return;
    }
    setStatus('saving');
    setError('');
    try {
      await fetchApi('/v1/hq/checklist', {
        method: 'POST',
        body: JSON.stringify({ email: trimmed, guide_slug: guideSlug }),
      });
      trackHq('HQ Guide Email Submitted', { guide: guideSlug });
      setStatus('done');
    } catch {
      setStatus('error');
      setError('Could not send the checklist. Try again in a minute.');
    }
  }

  if (status === 'done') {
    return (
      <p className='rounded-2xl bg-hq-success px-5 py-4 text-sm text-hq-ink'>
        Sent. Check {email} for “{guideTitle}”.
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className='rounded-2xl border border-hq-border bg-hq-paper px-5 py-4'>
      <p className='text-sm font-semibold text-hq-ink'>Email me this checklist</p>
      <p className='mt-1 text-sm text-hq-muted'>
        We will send the list and a link back here. No account. Not legal advice.
      </p>
      <div className='mt-3 flex flex-col gap-2'>
        <label className='sr-only' htmlFor={`hq-email-${guideSlug}`}>
          Email
        </label>
        <input
          id={`hq-email-${guideSlug}`}
          type='email'
          autoComplete='email'
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder='you@email.com'
          className='min-w-0 rounded-xl border border-hq-border-strong px-3 py-2 text-sm text-hq-ink outline-none focus:border-hq-sage'
        />
        <button
          type='submit'
          disabled={status === 'saving'}
          className='rounded-xl bg-hq-sage px-4 py-2 text-sm font-semibold text-white hover:bg-hq-sage-hover disabled:opacity-60'
        >
          {status === 'saving' ? 'Sending…' : 'Send checklist'}
        </button>
      </div>
      {error ? <p className='mt-2 text-sm text-hq-error'>{error}</p> : null}
    </form>
  );
}
